import React from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Button from 'Components/DesignSystem/Button/Button';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import { Cross } from 'Components/DesignSystem/Iconography/Icons';

type UnsavedChangesModalProps = {
    id: string;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    discardText: string;
    onClose: () => void;
    onConfirm: () => void;
    onDiscard: () => void;
    saveInProgress?: boolean;
}

/**
 * The shared unsaved-changes guard, on the DS Dialog (2026-09-06) — five
 * consumers (Mapping, CatalogManagement, Searchbar, MenuItem,
 * HierachicalDropdown) mount it conditionally with the same prop shape, so one
 * swap re-themes them all. The prop contract and every `${id}-*-btn` test hook
 * are unchanged; new behaviour is only what the DS shell brings: Escape closes
 * (routing through the same onClose as X/Cancel) and the confirm button wears
 * the § 209 loading grammar while `saveInProgress`. The scrim stays inert — a
 * dialog guarding unsaved edits must not discard them to a stray click.
 */
const UnsavedChangesModal = ({
    id,
    title,
    message,
    confirmText,
    cancelText,
    discardText,
    onClose,
    onConfirm,
    onDiscard,
    saveInProgress
}: UnsavedChangesModalProps) => {
    return (
        <Dialog
            isOpen
            onClose={onClose}
            title={title}
            /* `md`, not `sm` — the § 113 measurement from RuleChangeConfirmDialog
               (the Compatibility screen's sibling confirm): sm minus the footer
               inset leaves 336px, which THREE buttons do not clear — they wrapped
               to two lines each. md matches that dialog exactly. */
            size="md"
            data-testid={id}
            showCloseButton={false}
            headerActions={
                <IconButton
                    size="sm"
                    aria-label="Close"
                    id={`${id}-close-btn`}
                    data-testid={`${id}-close-btn`}
                    onClick={onClose}
                >
                    <Cross size={16} />
                </IconButton>
            }
            footer={
                /* Discard sits alone on the far LEFT — the destructive exit kept
                   apart from the safe Cancel/Save pair, so a hurried click on the
                   right side of the footer can never land on it. */
                <div id={`${id}-actions`} className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <Button
                        variant="danger"
                        className="tw-whitespace-nowrap"
                        id={`${id}-discard-btn`}
                        data-testid={`${id}-discard-btn`}
                        onClick={onDiscard}
                        disabled={saveInProgress}
                    >
                        {discardText}
                    </Button>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <Button
                            variant="secondary"
                            className="tw-whitespace-nowrap"
                            id={`${id}-cancel-btn`}
                            data-testid={`${id}-cancel-btn`}
                            onClick={onClose}
                            disabled={saveInProgress}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            variant="primary"
                            className="tw-whitespace-nowrap"
                            id={`${id}-confirm-btn`}
                            data-testid={`${id}-confirm-btn`}
                            onClick={onConfirm}
                            disabled={saveInProgress}
                            loading={saveInProgress}
                        >
                            {saveInProgress ? "Saving" : confirmText}
                        </Button>
                    </div>
                </div>
            }
        >
            <Alert tone="warning" data-testid={`${id}-message`}>
                {message}
            </Alert>
        </Dialog>
    );
}

export default UnsavedChangesModal;
